import { Injectable, inject } from "@angular/core";
import { Observable, of, Subscription } from "rxjs";
import { delay, switchMap, take } from "rxjs/operators";
import { FmsService } from "src/app/services/fms.service";
import { LoadingStateService } from "src/app/services/loading-state/loading-state.service";
import { ToastService } from "src/app/services/toast.service";

const DISPLAY_SNACKBAR_THRESHOLD = 0;

@Injectable({
  providedIn: "root",
})
export class FetchPrognosesSnackBarService {
  private readonly loadingStateService = inject(LoadingStateService);
  private readonly fmsService = inject(FmsService);
  private readonly toastService = inject(ToastService);

  // One subscription per page visit. The service is a root singleton, so a shared destroy$ that is
  // completed on leave could not stop the subscriptions of later visits.
  private subscription?: Subscription;

  public openSnackBarOnCurrentYearLoading(): void {
    this.subscription?.unsubscribe();
    this.subscription = this.loadingStateService.isLoaded$
      .pipe(
        /**
         * Prevent snack bar from being displayed when prognoses are loaded quickly
         *
         * switchMap() with delay() will stop all emissions when
         * isCurrentYearLoaded goes false -> true before the delay has expired,
         * and consequently prevent the snack bar from being displayed.
         * The text is fetched inside the switchMap too, so a "loaded" value also cancels a pending show.
         */
        switchMap((isCurrentYearLoaded) => (isCurrentYearLoaded ? of(null) : this.loadingText())),
      )
      .subscribe((loadingText) => (loadingText === null ? this.close() : this.open(loadingText)));
  }

  public destroy(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
    this.close();
  }

  private loadingText(): Observable<string> {
    return this.fmsService
      .translateAsync<string>("pensionPlan.snackBar.loading")
      .pipe(take(1), delay(DISPLAY_SNACKBAR_THRESHOLD));
  }

  private open(text: string): void {
    this.toastService.show({ text, duration: "always" });
  }

  private close(): void {
    this.toastService.hide();
  }
}
